<script setup>
import { ref, computed, onMounted, watch, reactive } from "vue";
import { useI18n } from "vue-i18n";
import { handleError } from '@/utils/errors';
import DialogConfirmDelet from "@/components/common/DialogConfirmDelet.vue";
import ButtonNew from "@/components/common/button/ButtonNew.vue";
import { useRoute, useRouter } from "vue-router";
import AdvancedFilter from "@/components/common/Filters/AdvancedFilter.vue";
import {
    deletePermissionsManagement,
    getAvailableActionsForRoute,
    getPermissionsManagement
} from "@/services/api/permissionManagement.js";
import { computeClass } from "@/utils/styleFormats.js";
import DialogConfirm from "@/components/common/DialogConfirm.vue";
import BaseTable from "@/components/tables/BaseTable.vue";
import {PermissionManagementEntity} from "@/entities/PermissionManagementEntity.js";
import BasePage from "@/components/Layouts/BasePage.vue";
import BaseAdvancedFilter from "@/components/common/Filters/BaseAdvancedFilter.vue";
import BaseQuickSearch from "@/components/common/Filters/BaseQuickSearch.vue";
import {getCorp} from "@/services/api/corporation.js";
import { configureSettingsEntityRoutes } from "@/utils/settingsRoutes.js";

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const permissionEntity = configureSettingsEntityRoutes(new PermissionManagementEntity(), route);
const entity = ref(permissionEntity);

const items = ref([]);
const totalItems = ref(0);
const loading = ref(false);
const currentPage = ref(1);
const itemsPerPage = ref(10);
const searchText = ref("");
const filters = ref([]);
const quickSearchConfig = computed(() => entity.value.pages.list.filters.quickSearch);
const actions = ref([]);
const havePermission = ref(false);
const showDialogConfirmDelet = ref(false);
const itemToDelete = ref(null)
const permissionManagementToDelete = ref(false);
const showDialogConfirm = ref(false);
const orderBy = ref(null);

async function onPageChange(page) {
    await fetchData({page: page});
}

async function onDelete(item) {
    itemToDelete.value = item;
    showDialogConfirmDelet.value = true;
}

async function onAdvancedFilter(filtersAdvanced) {
    filters.value = filtersAdvanced;
    await fetchData(filtersAdvanced);
}

async function onCleanFilter() {
    filters.value = null;
    await fetchData();
}

// <editor-fold desc="TableList">
const sortBy = ref([]);


async function fetchData(params = {}) {
    try {
        loading.value = true;
        const corp = getCorp();
        if (orderBy.value) {
            params['orderBy'] = orderBy.value.key;
        }
        currentPage.value = params.page ?? currentPage.value;
        if (searchText.value) {
            const field = quickSearchConfig.value.searchBy[0];
            params[field] = "%" + searchText.value + "%";
        }

        if (filters.value && Object.keys(filters.value).length > 0) {
            params = {
                ...filters.value,
                ...params
            };
        }

        const response = await getPermissionsManagement({
            limit: itemsPerPage.value,
            page: currentPage.value,
            ...params
        });

        items.value = response.data.items ?? response.data;
        totalItems.value = response.page;
    } catch (error) {
        console.error(error);
        message.error(await handleError(error, t));
    } finally {
        loading.value = false;
    }
}


// </editor-fold>/

// <editor-fold desc="Editar">

function onEdit(item) {
    router.push(entity.value.getUrl(entity.value.pages.edit.route?.at(-1), { id: item.id }));
}

// </editor-fold>/

// <editor-fold desc="Confirmar exclução">

const confirmDelete = async () => {
    try {
        showDialogConfirmDelet.value = false;
        await deletePermissionsManagement({id: itemToDelete.value.id});
        await fetchData();
        message.success(t('common.messages.success.itemDeletedSuccessfully'));
    } catch (error) {
        message.error(await handleError(error, t));
    }
};

const handleDelete = (item) => {
    itemToDelete.value = item;
    permissionManagementToDelete.value = itemToDelete.value;

    if(permissionManagementToDelete.value.qtdUsers > 0){
        showDialogConfirm.value = true;
    } else {
        showDialogConfirmDelet.value = true;
    }
};

async function onOrderBy(orderByArray) {
    orderBy.value = orderByArray;
}

// </editor-fold>/
onMounted(async () => {
    actions.value = await getAvailableActionsForRoute("Resources/Settings/Permission Management");
    if (!actions.value?.list?.length) {
        message.error(t("common.messages.errors.403"));
    } else {
        havePermission.value = true;
        await fetchData();
    }
});


</script>

<template>
    <BasePage :entity="entity">
        <v-row class="mb-4">
            <v-col>
                <ButtonNew  v-if="actions && actions.edit" :route="entity.pages.create.route?.at(-1)" />
            </v-col>
            <v-col class="base-advanced-filter">
                <BaseAdvancedFilter
                    :entity="entity"
                    @filter="onAdvancedFilter"
                    @clean="onCleanFilter"
                />
            </v-col>
            <v-col class="base-quick-search">
                <BaseQuickSearch
                    :entity="entity"
                    v-if="quickSearchConfig"
                    :config="quickSearchConfig"
                    v-model="searchText"
                    :fetchData="fetchData"
                />
            </v-col>
        </v-row>
        <BaseTable
            :fetchData="fetchData"
            :entity="entity"
            :items="items"
            :loading="loading"
            :totalItems="totalItems"
            :currentPage="currentPage"
            :itemsPerPage="itemsPerPage"
            :actions="actions"
            @edit="onEdit"
            @delete="handleDelete"
            @page-change="onPageChange"
            @onOrderBy="onOrderBy"
        >
            <template #status="{ row }">
                <span class="trezo-status-badge" :class="computeClass(row.status)" >
                    {{ t('common.labels.' + (row.status ? 'active' : 'inactive' )) }}
                </span>
            </template>
        </BaseTable>
        <DialogConfirmDelet
            v-model="showDialogConfirmDelet"
            :item="`${t('components.settings.permissionManagementList.theGroup')} ${permissionManagementToDelete?.name}`"
            @confirm="confirmDelete"
        />
        <DialogConfirm
            v-model="showDialogConfirm"
            type="error"
            :title="t('components.settings.permissionManagementList.titleMessageDeleteGroup')"
            :description="t('components.settings.permissionManagementList.descriptionMessageDeleteGroup')"
        />

    </BasePage>
</template>
